import { useEffect, useMemo, useRef } from "react";

import { Bar, Line } from "react-chartjs-2";

import type { ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { useUser } from "@components/user-context";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { TileType, type TileDescriptor } from "@features/overview-v2/types";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { type ITimeGranularityV2 } from "@north-beam/nb-common";

import {
  generateChartData,
  generateChartDataset,
  getChartOptions,
  type DataPoint,
} from "./utils";

interface Props {
  tile:
    | TileDescriptor<typeof TileType.BAR_CHART>
    | TileDescriptor<typeof TileType.LINE_CHART>
    | TileDescriptor<typeof TileType.CONVERSION_LAG>;
  metric1: SalesPageMetric;
  metric2?: SalesPageMetric;
  metric1Data: GetOverviewMetricsReport_me_overviewMetricsReport;
  metric2Data?: GetOverviewMetricsReport_me_overviewMetricsReport;
}

export const Chart = ({
  tile,
  metric1,
  metric2,
  metric1Data,
  metric2Data,
}: Props) => {
  const { user } = useUser();
  const {
    filters: { dateRangeInterval },
  } = useOverviewContext();

  const barChartRef = useRef<ChartJSOrUndefined<"bar", DataPoint[]>>(null);
  const lineChartRef = useRef<ChartJSOrUndefined<"line", DataPoint[]>>(null);

  useEffect(() => {
    setTimeout(() => {
      if (barChartRef.current) {
        barChartRef.current.resize();
      }
      if (lineChartRef.current) {
        lineChartRef.current.resize();
      }
    }, 50);
  }, []);

  const timeGranularity =
    metric1Data.effectiveTimeGranularity as ITimeGranularityV2;

  const chartDataset = useMemo(() => {
    const chartData = generateChartData({
      tileType: tile.type,
      metric1,
      metric2,
      metric1Data,
      metric2Data,
      dateRange: dateRangeInterval,
      timezone: user.timezone,
      timeGranularity,
      breakdowns: tile.breakdowns,
    });

    return generateChartDataset(tile, chartData, dateRangeInterval);
  }, [
    metric1,
    metric2,
    metric1Data,
    metric2Data,
    dateRangeInterval,
    user.timezone,
    timeGranularity,
    tile,
  ]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {tile.type === TileType.BAR_CHART ||
      tile.type === TileType.CONVERSION_LAG ? (
        <Bar
          ref={barChartRef}
          datasetIdKey="id"
          options={getChartOptions({
            tileType: tile.type,
            metric1,
            metric2,
            alignAxes: tile.alignAxes,
            data: chartDataset,
            timeGranularity,
            globalDateRange: dateRangeInterval,
            code: user.currencies.currentCurrency,
          })}
          data={chartDataset}
        />
      ) : (
        <Line
          ref={lineChartRef}
          datasetIdKey="id"
          options={getChartOptions({
            tileType: tile.type,
            metric1,
            metric2,
            alignAxes: tile.alignAxes,
            data: chartDataset,
            timeGranularity,
            globalDateRange: dateRangeInterval,
            code: user.currencies.currentCurrency,
          })}
          data={chartDataset}
        />
      )}
    </div>
  );
};
